import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { IAPIKey, IChangePassword, IConfirmAccountResetPassword, ICreateAccount, ICreateSubUser, ILogin, ILoginResponse, IRole, ISubUser, IUpdateAccount, IUpdateUserStatus } from "../models/account.model";
import { ResponseModel } from "src/app/shared/interfaces/response-model";

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.userManagementBaseUrl}/api/Account`;

  createUser(payload: ICreateAccount): Observable<ResponseModel<any>> {
    return this.http.post<ResponseModel<any>>(`${this.baseUrl}/create-user`, payload);
  }

  createSubUser(payload: ICreateSubUser): Observable<ResponseModel<any>> {
    return this.http.post<ResponseModel<any>>(`${this.baseUrl}/create-sub-user`, payload);
  }

  getSubUsers(authId?: string): Observable<ResponseModel<ISubUser[]>> {
    let params = new HttpParams();
    if (authId) params = params.set('authId', authId);
    return this.http.get<ResponseModel<ISubUser[]>>(`${this.baseUrl}/get-sub-users`, { params });
  }

  onboardUser(payload: ICreateAccount): Observable<ResponseModel<any>> {
    return this.http.post<ResponseModel<any>>(`${this.baseUrl}/onboard-user`, payload);
  }

  updateUserStatus(payload: IUpdateUserStatus): Observable<ResponseModel<any>> {
    return this.http.post<ResponseModel<any>>(`${this.baseUrl}/update-user-status`,payload); 
  }

  updateUserProfile(userId: string, payload: IUpdateAccount): Observable<ResponseModel<any>> {
    return this.http.patch<ResponseModel<any>>(`${this.baseUrl}/update-user-profile/${userId}`, payload);
  }

  getRoles(): Observable<ResponseModel<IRole>> {
    return this.http.get<ResponseModel<IRole>>(`${this.baseUrl}/get-roles`);
  }
  
  getSubRoles(): Observable<ResponseModel<IRole[]>> {
    return this.http.get<ResponseModel<IRole[]>>(`${this.baseUrl}/get-sub-roles`);
  }

  changePassword(payload: IChangePassword): Observable<ResponseModel<any>> {
    return this.http.post<ResponseModel<any>>(`${this.baseUrl}/change-password`, payload);
  }

  confirmAccount(email: string, token: string): Observable<ResponseModel<any>> {
    let params = new HttpParams();
    if (email) params = params.set('email', email);
    if (token) params = params.set('token', token);

    return this.http.get<ResponseModel<any>>(`${this.baseUrl}/confirm-account`, { params });
  }

  confirmAccountResetPassword(payload: IConfirmAccountResetPassword): Observable<ResponseModel<any>> {
    return this.http.post<ResponseModel<any>>(`${this.baseUrl}/confirm-account-resetpassword`, payload);
  }
  
  regenerateEmailConfirmationToken(email: string): Observable<string> {
    let params = new HttpParams();
    if (email) params = params.set('email', email);

    return this.http.get<string>(`${this.baseUrl}/regenerate-email-confirmation-token`, {
      params
    });
  }
  
  refreshToken(refreshToken: string): Observable<ResponseModel<ILoginResponse>> {
    let params = new HttpParams();
    if (refreshToken) params = params.set('refreshToken', refreshToken);

    return this.http.get<ResponseModel<ILoginResponse>>(`${this.baseUrl}/refresh-token`, { params });
  }

  generateApiKey(): Observable<ResponseModel<any>> {
    return this.http.post<ResponseModel<any>>(`${this.baseUrl}/generate-api-key`, null);
  }

  extendApiKey(): Observable<ResponseModel<any>> {
    return this.http.post<ResponseModel<any>>(`${this.baseUrl}/extend-api-key`, null);
  }

  getExistingApiKey(userId?: string): Observable<ResponseModel<IAPIKey>> {
    let params = new HttpParams();
    if (userId) params = params.set('userId', userId);

    return this.http.get<ResponseModel<IAPIKey>>(`${this.baseUrl}/get-existing-api-key`, { params });
  }

  setupTwoFactor(userId?: string): Observable<ResponseModel<any>> {
    let params = new HttpParams();
    if (userId) params = params.set('userId', userId);

    return this.http.get<ResponseModel<any>>(`${this.baseUrl}/setup-two-factor`, { params });
  }

  enableDisableTwoFactor(otp?: string, isEnabled?: boolean): Observable<ResponseModel<any>> {
    let params = new HttpParams();
    if (otp) params = params.set('otp', otp);
    if (isEnabled) params = params.set('isEnabled', isEnabled);

    return this.http.get<ResponseModel<any>>(`${this.baseUrl}/enable-disable-two-factor`, { params });
  }

  login(payload: ILogin): Observable<ResponseModel<ILoginResponse>> {
    return this.http.post<ResponseModel<ILoginResponse>>(`${this.baseUrl}/login`, payload);
  }

  verifyOtpForLogin(email?: string, token?: string): Observable<ResponseModel<any>> {
    let params = new HttpParams();
    if (email) params = params.set('email', email);
    if (token) params = params.set('token', token);

    return this.http.post<ResponseModel<any>>(`${this.baseUrl}/verify-otp-for-login`, null, { params });
  }

  logoutUser(): Observable<ResponseModel<string>> {
    return this.http.post<ResponseModel<string>>(`${this.baseUrl}/logout-User`, null);
  }

  verifyOtp(email?: string, token?: string): Observable<ResponseModel<any>> {
    let params = new HttpParams();
    if (email) params = params.set('email', email);
    if (token) params = params.set('token', token);

    return this.http.post<ResponseModel<any>>(`${this.baseUrl}/verify-otp`, null, { params });
  }

  forgotPassword(email: string): Observable<ResponseModel<any>> {
    let params = new HttpParams();
    if (email) params = params.set('email', email);

    return this.http.get<ResponseModel<any>>(`${this.baseUrl}/forgot-password`, { params });
  }

  passwordReset(email: string, token: string, newPassword: string): Observable<ResponseModel<any>> {
    let params = new HttpParams();
    if (email) params = params.set('email', email);
    if (token) params = params.set('token', token);
    if (newPassword) params = params.set('newPassword', newPassword);

    return this.http.get<ResponseModel<any>>(`${this.baseUrl}/password-reset`, { params });
  }
}
